import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";

import {
  ExportFormat,
  ReportFilterOption,
  ReportRow,
  ReportsData,
  ReportsFilters,
  ReportTab,
} from "../types";

const normalizeFilters = (filters: ReportsFilters = {}) => ({
  fromDate: filters.fromDate || undefined,
  toDate: filters.toDate || undefined,
  warehouseId: filters.warehouseId || undefined,
  categoryId: filters.categoryId || undefined,
  supplierId: filters.supplierId || undefined,
});

const toCurrency = (value: unknown) => Number(value || 0);

const text = (...values: unknown[]) =>
  values
    .map((value) => (value === null || value === undefined ? "" : String(value)))
    .find(Boolean) || "";

const isInternalId = (value?: string | null) =>
  !!value &&
  (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ||
    /^cm[a-z0-9]{18,}$/i.test(value));

const employeeDisplay = (employee: any) => {
  const employeeCode = text(employee.employeeCode, employee.code, employee.employeeId);
  const employeeName = text(
    employee.employeeName,
    employee.fullName,
    employee.name,
    `${employee.firstName || ""} ${employee.lastName || ""}`.trim()
  );

  if (employeeCode && employeeName) return `${employeeName} (${employeeCode})`;
  return employeeName || employeeCode || "";
};

const employeeLookup = (employees: any[]) => {
  const lookup = new Map<string, string>();

  employees.forEach((employee) => {
    const display = employeeDisplay(employee);
    if (!display) return;

    [
      employee.id,
      employee.employeeId,
      employee.employeeCode,
      employee.employeeRecordId,
      employee.userId,
    ]
      .filter(Boolean)
      .forEach((key) => lookup.set(String(key), display));
  });

  return lookup;
};

const toCsv = (rows: ReportRow[]) => {
  if (!rows.length) return "";

  const headers = Object.keys(rows[0]);
  const escape = (value: unknown) =>
    `"${String(value ?? "").replace(/"/g, '""')}"`;

  return [
    headers.join(","),
    ...rows.map((row) => headers.map((header) => escape(row[header])).join(",")),
  ].join("\n");
};

class ReportsService {
  async getOptions(): Promise<{
    hubs: ReportFilterOption[];
    categories: ReportFilterOption[];
    suppliers: ReportFilterOption[];
  }> {
    const [hubs, categories, suppliers] = await Promise.all([
      sparePartsApi
        .get<ApiResponse<any[]>>("/integrations/hubs")
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<any[]>>("/categories", {
          params: { page: 1, limit: 100 },
        })
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<any[]>>("/suppliers", {
          params: { page: 1, limit: 100 },
        })
        .catch(() => ({ data: { data: [] } })),
    ]);

    return {
      hubs: (hubs.data.data || []).map((hub) => ({
        id: hub.id,
        name: hub.code ? `${hub.name} (${hub.code})` : hub.name,
        code: hub.code,
      })),
      categories: (categories.data.data || []).map((category) => ({
        id: category.id,
        name: category.name,
        code: category.code,
      })),
      suppliers: (suppliers.data.data || []).map((supplier) => ({
        id: supplier.id,
        name: supplier.supplierName || supplier.name,
        code: supplier.code,
      })),
    };
  }

  async getReports(filters: ReportsFilters = {}): Promise<ReportsData> {
    const params = normalizeFilters(filters);

    const [
      dashboard,
      inventory,
      procurement,
      maintenance,
      reports,
      stocks,
      spareReturns,
      hubs,
      technicians,
      ames,
    ] = await Promise.all([
      sparePartsApi.get<ApiResponse<any>>("/analytics/dashboard", { params }),
      sparePartsApi.get<ApiResponse<any>>("/analytics/inventory", { params }),
      sparePartsApi.get<ApiResponse<any>>("/analytics/procurement", { params }),
      sparePartsApi.get<ApiResponse<any>>("/analytics/maintenance", { params }),
      sparePartsApi.get<ApiResponse<any>>("/analytics/reports", { params }),
      sparePartsApi.get<ApiResponse<any[]>>("/inventory/stocks", {
        params: {
          page: 1,
          limit: 1000,
          warehouseId: params.warehouseId,
          categoryId: params.categoryId,
        },
      }),
      sparePartsApi
        .get<ApiResponse<any[]>>("/maintenance/spare-returns", {
          params: {
            page: 1,
            limit: 1000,
            warehouseId: params.warehouseId,
          },
        })
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<any[]>>("/integrations/hubs")
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<any[]>>("/integrations/technicians")
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<any[]>>("/integrations/ames")
        .catch(() => ({ data: { data: [] } })),
    ]);

    const dashboardData = dashboard.data.data || {};
    const inventoryData = inventory.data.data || {};
    const procurementData = procurement.data.data || {};
    const maintenanceData = maintenance.data.data || {};
    const reportsData = reports.data.data || {};
    const stockRows = stocks.data.data || [];
    const hubNameById = new Map(
      (hubs.data.data || []).map((hub: any) => [
        hub.id,
        hub.code ? `${hub.name} (${hub.code})` : hub.name,
      ])
    );
    const warehouseName = (warehouseId?: string | null) =>
      (warehouseId && hubNameById.get(warehouseId)) || warehouseId || "-";
    const employeeNameById = new Map([
      ...employeeLookup(technicians.data.data || []),
      ...employeeLookup(ames.data.data || []),
    ]);
    const employeeName = (employeeId?: string | null) => {
      const value = employeeId || "";
      return employeeNameById.get(value) || (isInternalId(value) ? "-" : value || "-");
    };

    const inventoryReport: ReportRow[] = (inventoryData.warehouseWise || []).map(
      (row: any) => ({
        id: row.warehouseId,
        warehouse: warehouseName(row.warehouseId),
        totalParts: row.totalParts,
        availableStock: row.availableStock,
        reservedStock: row.reservedStock,
        damagedStock: row.damagedStock,
        inventoryValue: row.inventoryValue,
      })
    );

    const purchaseReport: ReportRow[] = (procurementData.monthlyPurchase || []).map(
      (row: any) => ({
        id: row.month,
        month: row.month,
        bills: row.bills,
        purchaseCost: row.purchaseCost,
      })
    );

    const maintenanceReport: ReportRow[] = [
      {
        id: "maintenance-cost-summary",
        reportType: "Cost Summary",
        completedTickets: maintenanceData.completedTickets || 0,
        sparePartsCost: maintenanceData.invoices?.sparePartsCost || 0,
        repairServiceCost: maintenanceData.invoices?.repairServiceCost || 0,
        totalMaintenanceCost:
          maintenanceData.invoices?.totalMaintenanceCost ||
          maintenanceData.invoices?.invoiceValue ||
          0,
        costPerCompletedTicket:
          maintenanceData.invoices?.costPerCompletedTicket || 0,
      },
      ...(maintenanceData.serviceTypeSummary || []).map((row: any) => ({
        id: row.serviceType,
        reportType: "Service Type",
        serviceType: row.serviceType,
        tickets: row.tickets,
      })),
      ...(maintenanceData.technicianWorkload || []).map((row: any) => ({
        id: row.technicianId,
        reportType: "Technician Workload",
        technician: employeeName(row.technicianId),
        assignments: row.assignments,
        completedTickets: row.completedTickets,
        openTickets: row.openTickets,
      })),
    ];

    const supplierReport: ReportRow[] = (procurementData.supplierPerformance || []).map(
      (row: any) => ({
        id: row.supplierId,
        supplierName: row.supplierName,
        bills: row.bills,
        completedBills: row.completedBills,
        completionRate: row.completionRate,
        purchaseCost: row.purchaseCost,
      })
    );

    const vehicleReport: ReportRow[] = (reportsData.monthly || []).map((row: any) => ({
      id: row.month,
      period: row.month,
      tickets: row.tickets,
      completedTickets: row.completedTickets,
      inwardQuantity: row.inwardQuantity,
      issueQuantity: row.issueQuantity,
    }));

    const lowStockReport: ReportRow[] = stockRows
      .filter((row: any) => Number(row.availableQuantity || 0) <= Number(row.reorderLevel || 0))
      .map((row: any) => ({
        id: row.id,
        sparePart: row.sparePart?.name || row.sparePartId,
        code: row.sparePart?.code || row.code,
        warehouse: warehouseName(row.warehouseId),
        availableQuantity: row.availableQuantity,
        reorderLevel: row.reorderLevel,
        minimumStock: row.minimumStock,
      }));

    const spareReturnReport: ReportRow[] = (spareReturns.data.data || []).flatMap(
      (row: any) =>
        (row.items || []).map((item: any) => ({
          id: `${row.id}-${item.id}`,
          returnCode: row.returnCode,
          ticket: row.ticket?.code || row.ticketId,
          technician: employeeName(row.technicianId),
          warehouse: warehouseName(row.warehouseId),
          sparePart: item.sparePart?.name || item.sparePartId,
          issuedQty: item.issuedQty,
          usedQty: item.usedQty,
          returnedQty: item.returnedQty,
          acceptedQty: item.acceptedQty,
          damagedQty: item.damagedQty,
          status: row.status,
          verifiedBy: employeeName(row.verifiedBy),
          verifiedAt: row.verifiedAt
            ? new Date(row.verifiedAt).toLocaleDateString()
            : "-",
        }))
    );

    return {
      summary: {
        inventoryValue: toCurrency(dashboardData.inventoryValue),
        totalStock: Number(dashboardData.totalStock || 0),
        lowStock: Number(dashboardData.lowStock || 0),
        purchaseCost: toCurrency(procurementData.purchaseCost),
        openTickets: Number(maintenanceData.openTickets || 0),
        suppliers: Number(dashboardData.totalSuppliers || 0),
      },
      inventoryReport,
      purchaseReport,
      maintenanceReport,
      supplierReport,
      vehicleReport,
      spareReturnReport,
      lowStockReport,
    };
  }

  exportRows(rows: ReportRow[], tab: ReportTab, format: ExportFormat) {
    const content =
      format === "json" ? JSON.stringify(rows, null, 2) : toCsv(rows);
    const type = format === "json" ? "application/json" : "text/csv";
    const blob = new Blob([content], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `spare-parts-${tab}-report.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export default new ReportsService();
